import { useState, useRef, useEffect } from "react";
import { ScanLine, Upload, Loader2, CheckCircle2, RotateCcw, Sparkles, Camera, Smartphone, X } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { playNotificationSound } from "@/lib/notify";
import { useLang } from "@/context/LanguageContext";

export function CarteGriseScanner({ onDetected }) {
  const { t } = useLang();
  const [status, setStatus] = useState("idle"); // idle | scanning | done | error
  const [mode, setMode] = useState("menu"); // menu | qr
  const [sides, setSides] = useState({ recto: null, verso: null });
  const [vehicle, setVehicle] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const activeSide = useRef("recto");

  const FIELDS = [
    ["marque", t("scanner.f_marque")],
    ["modele", t("scanner.f_modele")],
    ["immatriculation", t("scanner.f_immat")],
    ["vin", t("scanner.f_vin")],
    ["annee", t("booking.v_annee")],
    ["cylindree", t("booking.v_cylindree")],
    ["energie", t("scanner.f_energie")],
    ["couleur", t("scanner.f_couleur")],
  ];

  const applyVehicle = (v) => {
    setVehicle(v);
    setStatus("done");
    setMode("menu");
    onDetected?.(v);
    toast.success(t("scanner.toast_ok"));
  };

  const pick = (side, type) => {
    activeSide.current = side;
    (type === "cam" ? camRef : fileRef).current?.click();
  };

  const onFile = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("scanner.toast_format"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSides((s) => ({ ...s, [activeSide.current]: reader.result }));
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
    if (camRef.current) camRef.current.value = "";
  };

  const analyze = async () => {
    if (!sides.recto || !sides.verso) { toast.error(t("scanner.need_both")); return; }
    setStatus("scanning");
    try {
      const { data } = await api.post("/scan-carte-grise", { images: [sides.recto, sides.verso] });
      applyVehicle(data.vehicle);
    } catch (e) {
      setStatus("error");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startPhoneScan = async () => {
    try {
      const { data } = await api.post("/scan-sessions");
      setSessionId(data.id);
      setMode("qr");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (mode !== "qr" || !sessionId) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/scan-sessions/${sessionId}`);
        if (data.status === "done" && data.vehicle) {
          clearInterval(id);
          playNotificationSound();
          if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
          applyVehicle(data.vehicle);
        } else if (data.status === "expired") {
          clearInterval(id);
          setMode("menu");
          toast.error(t("scanner.mobile_expired"));
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sessionId]);

  const reset = () => {
    setStatus("idle");
    setMode("menu");
    setSides({ recto: null, verso: null });
    setVehicle(null);
    setSessionId(null);
    if (fileRef.current) fileRef.current.value = "";
    if (camRef.current) camRef.current.value = "";
  };

  const mobileUrl = sessionId ? `${window.location.origin}/scan-mobile/${sessionId}` : "";
  const bothReady = sides.recto && sides.verso;

  const SIDES = [
    { key: "recto", label: t("scanner.side_recto") },
    { key: "verso", label: t("scanner.side_verso") },
  ];

  return (
    <div className="w-full" data-testid="carte-grise-scanner">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="carte-grise-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="carte-grise-camera-input" onChange={(e) => onFile(e.target.files?.[0])} />

      {status === "idle" && mode === "menu" && (
        <div className="relative overflow-hidden border border-[#00E5FF]/25 bg-[#050505] p-6 sm:p-8 shadow-[0_0_25px_rgba(0,229,255,0.06)]" data-testid="carte-grise-menu">
          <span className="corner corner-tl" /><span className="corner corner-tr" /><span className="corner corner-bl" /><span className="corner corner-br" />
          <div className="relative z-10 flex flex-col items-center gap-5 text-center">
            <div>
              <p className="font-display font-700 text-white text-lg tracking-tight">{t("scanner.title")}</p>
              <p className="text-zinc-500 text-sm mt-1 max-w-sm mx-auto">{t("scanner.two_sides_hint")}</p>
            </div>

            <div className="w-full grid sm:grid-cols-2 gap-4 mt-1">
              {SIDES.map((s) => (
                <div key={s.key} className="border border-white/12 bg-[#0C0C0E] p-3" data-testid={`carte-grise-side-${s.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.14em] text-[#00E5FF]">{s.label}</span>
                    {sides[s.key] && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  {sides[s.key] ? (
                    <div className="relative h-28 overflow-hidden border border-white/10">
                      <img src={sides[s.key]} alt={s.key} className="w-full h-full object-cover" />
                      <button onClick={() => setSides((prev) => ({ ...prev, [s.key]: null }))} data-testid={`carte-grise-clear-${s.key}`} className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center bg-black/70 text-white hover:bg-[#E60000] transition-colors"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => pick(s.key, "cam")} data-testid={`carte-grise-cam-${s.key}`} className="flex flex-col items-center gap-1.5 py-4 border border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/5 transition-colors">
                        <Camera className="w-5 h-5 text-[#00E5FF]" />
                        <span className="font-mono-tech text-[9px] uppercase tracking-[0.12em] text-white">{t("scanner.cap_photo")}</span>
                      </button>
                      <button type="button" onClick={() => pick(s.key, "file")} data-testid={`carte-grise-file-${s.key}`} className="flex flex-col items-center gap-1.5 py-4 border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors">
                        <Upload className="w-5 h-5 text-zinc-300" />
                        <span className="font-mono-tech text-[9px] uppercase tracking-[0.12em] text-white">{t("scanner.cap_file")}</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button type="button" onClick={analyze} disabled={!bothReady} data-testid="carte-grise-analyze" className="w-full inline-flex items-center justify-center gap-2 py-3.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <ScanLine className="w-4 h-4" /> {bothReady ? t("scanner.analyze") : t("scanner.need_both")}
            </button>

            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-600">{t("scanner.or")}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <button type="button" onClick={startPhoneScan} data-testid="carte-grise-phone-btn" className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[#E60000]/40 hover:border-[#E60000] hover:bg-[#E60000]/5 transition-colors">
              <Smartphone className="w-5 h-5 text-[#E60000]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-white">{t("scanner.from_phone")}</span>
            </button>
          </div>
        </div>
      )}

      {status === "idle" && mode === "qr" && (
        <div className="relative border border-[#00E5FF]/30 bg-[#050505] p-8 text-center shadow-[0_0_25px_rgba(0,229,255,0.08)]" data-testid="carte-grise-qr">
          <button onClick={reset} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="carte-grise-qr-cancel"><X className="w-5 h-5" /></button>
          <h3 className="font-display font-700 text-white text-lg tracking-tight flex items-center justify-center gap-2"><Smartphone className="w-5 h-5 text-[#E60000]" /> {t("scanner.qr_title")}</h3>
          <p className="text-zinc-500 text-sm mt-2 max-w-sm mx-auto">{t("scanner.qr_hint")}</p>
          <div className="inline-block mt-6 p-4 bg-white rounded-xl">
            {mobileUrl && <QRCodeCanvas value={mobileUrl} size={196} bgColor="#ffffff" fgColor="#050505" level="M" data-testid="carte-grise-qr-code" />}
          </div>
          <div className="mt-6 flex items-center justify-center gap-3 font-mono-tech text-[#00E5FF] text-xs uppercase tracking-[0.2em]">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("scanner.qr_waiting")}
          </div>
        </div>
      )}

      {status === "scanning" && (
        <div className="relative border border-[#00E5FF]/40 bg-[#050505] overflow-hidden" data-testid="carte-grise-scanning">
          {sides.recto && <img src={sides.recto} alt="carte grise" className="w-full max-h-[320px] object-contain opacity-70" />}
          <div className="scan-line" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="flex items-center gap-3 font-mono-tech text-[#00E5FF] text-sm uppercase tracking-[0.2em]">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("scanner.analyzing")}
            </div>
          </div>
        </div>
      )}

      {status === "done" && vehicle && (
        <div className="border border-[#00E5FF]/30 bg-[#050505] shadow-[0_0_25px_rgba(0,229,255,0.08)] fade-up" data-testid="carte-grise-result">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#00E5FF]/20">
            <div className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00E5FF]">
              <ScanLine className="w-4 h-4" /> {t("scanner.detected")}
            </div>
            <button onClick={reset} className="text-zinc-500 hover:text-white transition-colors flex items-center gap-1.5 text-xs" data-testid="carte-grise-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" /> {t("scanner.rescan")}
            </button>
          </div>
          <div className="p-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 font-mono-tech text-sm">
            {FIELDS.map(([key, label]) => (
              <div key={key} className="flex flex-col gap-0.5 border-b border-white/5 pb-2">
                <span className="text-[10px] text-zinc-600 tracking-[0.15em]">{label}</span>
                <span className={vehicle[key] ? "text-white" : "text-zinc-700"}>{vehicle[key] || "—"}</span>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-[#00E5FF]/20 flex items-center gap-2 text-[#00E5FF] text-xs font-mono-tech">
            <CheckCircle2 className="w-4 h-4" /> {t("scanner.prefilled")}
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="border border-[#E60000]/40 bg-[#E60000]/5 p-8 text-center" data-testid="carte-grise-error">
          <p className="text-white font-display font-700">{t("scanner.err_title")}</p>
          <p className="text-zinc-400 text-sm mt-1">{t("scanner.err_desc")}</p>
          <button onClick={reset} className="mt-4 px-5 py-2 border border-white/20 hover:border-white/50 text-white text-sm transition-colors" data-testid="carte-grise-retry-btn">
            {t("scanner.retry")}
          </button>
        </div>
      )}
    </div>
  );
}
